/**
 * Simple English pluralization: "1 item" vs "3 items".
 * Pass an irregular plural as the third argument (e.g. "person", "people").
 */
export function pluralize(count: number, singular: string, plural?: string): string {
  return `${count} ${count === 1 ? singular : (plural ?? `${singular}s`)}`
}

/** Parse an ISO date string (YYYY-MM-DD) as local midnight. */
function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number)
  return new Date(y, m - 1, d)
}

/** Local midnight today. */
function todayMidnight(): Date {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

/** Relative time label: "today", "3d ago", "2w ago", "3mo ago". */
export function timeAgo(isoDate: string): string {
  const days = Math.round(
    (todayMidnight().getTime() - parseLocalDate(isoDate).getTime()) / 86_400_000,
  )
  if (days <= 0) return "today"
  if (days === 1) return "1d ago"
  if (days < 7) return `${days}d ago`
  const weeks = Math.floor(days / 7)
  if (weeks < 5) return `${weeks}w ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(days / 365)}y ago`
}

/** Absolute date: "Apr 13, 2026". */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

/** Due-date label with graduated urgency: "Due in 3d", "Due today", "5d overdue". */
export function dueLabel(isoDate: string): string {
  const diff = Math.round(
    (parseLocalDate(isoDate).getTime() - todayMidnight().getTime()) / 86_400_000,
  )
  if (diff < 0) return `${Math.abs(diff)}d overdue`
  if (diff === 0) return "Due today"
  if (diff === 1) return "Due tomorrow"
  if (diff <= 14) return `Due in ${diff}d`
  return `Due ${parseLocalDate(isoDate).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
}
